import './components/_globals';
import '@icons/components';
import 'intersection-observer';
import 'vue-svgicon/dist/polyfill';
import * as Integrations from '@sentry/integrations';
import * as Sentry from '@sentry/vue';
import Vue, { VNode } from 'vue';
import AnimationRevealDirective from './directives/reveal';
import App from './App.vue';
import { Component } from 'vue-property-decorator';
import LazyLoadDirective from './directives/lazy-src';
import router from './router';
import store from './store';
import SvgIcon from 'vue-svgicon';
import { Toast } from 'types/layout/';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import VueCookies from 'vue-cookies';
import Vuelidate from 'vuelidate';

interface FundriseApp {
	apiClientId: string;
	checkoutMixpanelProperties: object;
	googleRecaptchaSiteKey: string;
	googleRecaptchaSiteKeyv3: string;
	initialToasts: Array<Toast>;
	investmentEntityId: string;
	mapboxToken: string;
	oAuthToken: string;
	requiresAdditionalAuthentication: boolean;
	recurringInvestmentEligibility: string;
	enableConversionPixels: string;
	passwordResetKey: string;
}

/*eslint-disable @typescript-eslint/no-explicit-any */
declare global {
	interface Window {
		fundriseApp: FundriseApp;
		Calendly: any;
		dotq: any;
		fbq: any;
		ga: any;
		google: any;
		grecaptchaV2: any;
		grecaptchaV3: any;
		gtag: any;
		letterpress: any;
		mixpanel: any;
		pdst: any;
		rdt: any;
		twttr: any;
		uetq: any;
		Plaid: any;
	}
}
/*eslint-enable @typescript-eslint/no-explicit-any */

Vue.config.productionTip = false;
Vue.config.ignoredElements = ['amp-story-player'];

Component.registerHooks(['validations', 'beforeRouteEnter', 'beforeRouteLeave']);

Vue.use(Vuelidate);
Vue.use(VueCookies);

Vue.use(SvgIcon, {
	tagName: 'SvgIcon',
	isOriginalDefault: true
});

Vue.directive('reveal', AnimationRevealDirective);
Vue.directive('lazy', LazyLoadDirective);

if (process.env.VUE_APP_SENTRY_ENABLED) {
	Sentry.init({
		Vue,
		dsn: `${process.env.VUE_APP_SENTRY_DSN}`,
		environment: `${process.env.VUE_APP_SENTRY_ENVIRONMENT}`,
		attachProps: false,
		trackComponents: true,
		logErrors: true,
		integrations: [
			new Integrations.SessionTiming(),
			new TracingIntegrations.BrowserTracing({
				routingInstrumentation: Sentry.vueRouterInstrumentation(router)
			})
		],
		tracesSampleRate: 0.025,
		beforeSend(event, hint) {
			const errorStringToFilter = ['Timeout'];

			if (hint && hint.originalException) {
				if (typeof hint?.originalException === 'string') {
					const bearerRegEx = /"Bearer([\s\S]+?)"/gi;
					hint.originalException = (hint.originalException as string).replace(bearerRegEx, '"Bearer XXX"');

					if (errorStringToFilter.some((errString) => errString === hint.originalException)) {
						return null;
					}
					/* eslint-disable @typescript-eslint/no-explicit-any */
				} else if ((hint?.originalException as any)?.config?.headers?.['authorization']) {
					(hint.originalException as any).config.headers['authorization'] = 'XXX';
				}
				/* eslint-enable @typescript-eslint/no-explicit-any */
			}

			if (event.request?.headers?.['authorization']) {
				event.request.headers['authorization'] = 'XXX';
			}

			const minimumDurationToSend = 1000;
			const sessionDuration = (event?.extra?.['session:duration'] as number) ?? minimumDurationToSend;

			if (sessionDuration < minimumDurationToSend) {
				return null;
			}

			return event;
		},
		ignoreErrors: [
			'__firefox__.metadata.extractMetadata',
			'__firefox__.favicons.getFavicons',
			'__firefox__.searchQueryForField',
			'SymBrowser_ModifyAnchorTagWithTarget',
			"Cannot read property 'Logger' of undefined",
			'Blocked a frame with origin "https://fundrise.com" from accessing a frame with origin "https://s.amazon-adsystem.com"',
			'Uncaught ReferenceError: ztePageScrollModule is not defined',
			'vid_mate_check',
			'PADDINGPADDINGXX',
			'__gCrWeb.autofill.extractForms',
			'fuckAdBlock',
			'blockAdBlock',
			'get_mSecure_Captured_Username',
			'_isMatchingDomain is not defined',
			'l.LegacyGlobal.should_do_lastpass_here is not a function.',
			'UET is not defined',
			'ttd_dom_ready',
			'ttd_dom_ready is not defined',
			'Network request failed',
			'Failed to fetch',
			'NetworkError',
			'Network Error',
			'The play method is not allowed by the user agent.',
			'window.mraidbridge',
			'webkit.messageHandlers',
			"'UET' is not defined",
			"Can't find variable: mraid",
			'timeout of 0ms exceeded',
			'/Blocked a frame with origin/',
			'Cannot find function isEqualNode in object',
			'Object Not Found Matching Id',
			'acsbJS',
			"'acsbJS'",
			'acsb',
			'acsbapp.com',
			'acsbChildFrameReady',
			'_isSmallFakeHeading',
			/.*_isSmallFakeHeading.*/i,
			'_createForOfIteratorHelper',
			'Symbol(Symbol.iterator)',
			'cancelled',
			'InvalidStateError: The object is in an invalid state.',
			'Request aborted',
			'Non-Error exception captured',
			'Non-Error promise rejection captured',
			"'fetch' is undefined",
			'reCaptcha has not been loaded yet.',
			"undefined is not an object (evaluating 'window.grecaptchaV2.ready')",
			"Cannot read properties of undefined (reading 'ready')",
			"Cannot read property 'ready' of undefined",
			'window.grecaptchaV2 is undefined',
			'Loading chunk',
			'Loading CSS chunk',
			'ChunkLoadError',
			'Timeout',
			'NotAllowedError',
			'The request is not allowed',
			"Cannot read properties of undefined (reading 'sendEvents')",
			'ping',
			"getElementsByTagName('video')",
			'fetch',
			'sendEvents',
			'CHzr/R.prototype.handlePageWillLeave(i18n/pixel/events)',
			"Cannot read properties of undefined (reading 'methods')",
			'PaymentAutofillConfig',
			"Unexpected token '='"
		],
		denyUrls: [
			/d\.impactradius-event\.com/i,
			// Facebook flakiness
			/graph\.facebook\.com/i,
			// Facebook blocked
			/connect\.facebook\.net\/en_US\/all\.js/i,
			// Woopra flakiness
			/eatdifferent\.com\.woopra-ns\.com/i,
			/static\.woopra\.com\/js\/woopra\.js/i,
			// Chrome extensions
			/extensions\//i,
			/^chrome:\/\//i,
			// Other plugins
			/127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
			/webappstoolbarba\.texthelp\.com\//i,
			/metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
			/maps\.googleapis\.com\/maps\/api/i,
			/gstatic\.com\/recaptcha\/releases/i,
			/@safari-extension/i,
			/@safari-web-extension/i,
			/https:\/\/(www\.)?fmdumps\.tools\.yodlee\.com\/404/i
		]
	});
	Sentry.setTag('Application', 'Vue');
}

new Vue({
	router,
	store,
	render: (h): VNode => h(App)
}).$mount('#app');
